import { useEffect, useMemo, useState } from "react";
import { getAllStaff } from "../../api/staff/staffApi";
import { getAllStaffPortal } from "../../api/staff/staffPortalApi";
import { getAllRoles } from "../../api/roles/roleApi";
import { getAllRolesPortal } from "../../api/roles/rolePortalApi";
import { pickList, toId } from "../../utils/examsschedule/examHelpers";

/**
 * Invigilator / staff picker: filter roles first, then staff by roleId.
 * No free-text staff id.
 *
 * preferredRoleNames — role names matched case-insensitively (e.g. Teacher, Invigilator)
 */
export default function StaffByRoleSelect({
  isPortal = false,
  value = [],
  onChange,
  disabled = false,
  preferredRoleNames = ["Teacher", "Invigilator", "Staff"],
  multi = true,
  label = "Invigilators",
}) {
  const [roles, setRoles] = useState([]);
  const [staff, setStaff] = useState([]);
  const [roleId, setRoleId] = useState("");
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [loadingStaff, setLoadingStaff] = useState(false);

  const selected = useMemo(
    () => (Array.isArray(value) ? value.map(toId) : value ? [toId(value)] : []),
    [value]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingRoles(true);
        const res = isPortal ? await getAllRolesPortal() : await getAllRoles();
        if (cancelled) return;
        const list = Array.isArray(res)
          ? res
          : pickList(res, ["roles", "data"]);
        setRoles(list);
        // Auto-pick preferred role
        const preferred = list.find((r) =>
          preferredRoleNames.some(
            (n) =>
              (r.roleName || r.name || "")
                .toLowerCase()
                .includes(n.toLowerCase())
          )
        );
        if (preferred) setRoleId(toId(preferred));
        else if (list[0]) setRoleId(toId(list[0]));
      } catch (e) {
        if (!cancelled) setRoles([]);
      } finally {
        if (!cancelled) setLoadingRoles(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isPortal]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!roleId) {
      setStaff([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoadingStaff(true);
        // Staff model field is `active` — backend supports ?active=true|false
        const res = isPortal
          ? await getAllStaffPortal({ roleId, active: "true" })
          : await getAllStaff({ roleId, active: "true" });
        if (cancelled) return;
        const list = Array.isArray(res)
          ? res
          : pickList(res, ["staff", "data"]);
        setStaff(list.filter((s) => s.active !== false));
      } catch (e) {
        if (!cancelled) setStaff([]);
      } finally {
        if (!cancelled) setLoadingStaff(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [roleId, isPortal]);

  const toggle = (id) => {
    if (disabled) return;
    if (!multi) {
      onChange?.(id);
      return;
    }
    const set = new Set(selected);
    if (set.has(id)) set.delete(id);
    else set.add(id);
    onChange?.([...set]);
  };

  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-gray-500 dark:text-neutral-400">{label}</label>
      <select
        className="w-full border border-gray-300 dark:border-neutral-700 rounded-lg px-3 py-2 text-sm"
        value={roleId}
        disabled={disabled || loadingRoles}
        onChange={(e) => setRoleId(e.target.value)}
      >
        <option value="">
          {loadingRoles ? "Loading roles…" : "Filter by role"}
        </option>
        {roles.map((r) => (
          <option key={toId(r)} value={toId(r)}>
            {r.roleName || r.name}
          </option>
        ))}
      </select>

      <div className="max-h-32 overflow-y-auto border rounded-lg p-2 space-y-1">
        {loadingStaff && (
          <p className="text-xs text-gray-400 dark:text-neutral-500">Loading staff…</p>
        )}
        {!loadingStaff && staff.length === 0 && (
          <p className="text-xs text-gray-400 dark:text-neutral-500">
            {roleId
              ? "No active staff for this role"
              : "Select a role first"}
          </p>
        )}
        {staff.map((st) => {
          const id = toId(st);
          return (
            <label key={id} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type={multi ? "checkbox" : "radio"}
                name={multi ? undefined : "staff-by-role"}
                disabled={disabled}
                checked={selected.includes(id)}
                onChange={() => toggle(id)}
              />
              {st.name}
              {st.staffCode ? ` (${st.staffCode})` : ""}
            </label>
          );
        })}
      </div>
      {multi && selected.length > 0 && (
        <p className="text-[11px] text-gray-500 dark:text-neutral-400">
          Selected: {selected.length}
        </p>
      )}
    </div>
  );
}
